import { useFormContext } from "react-hook-form";

import FormFieldViewText from "components/FormFields/ViewText";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import { BlueprintInput, TemplateVersion } from "types/generated";
import useAnnounce from "hooks/useAnnounce";

import DeploymentFormField from "./FormField";
import TemplatePreviewSkeleton from "./Skeleton";
import styles from "./styles.module.css";
import { SecretFieldsState, VersionTemplateFormFields } from "./types";

type DeploymentFormFormProps = {
  item: TemplateVersion;
  isPublished: boolean;
  hasErrors: boolean;
  hasPreview: boolean;
  loading?: boolean;
  errors: string[];
  inputs: BlueprintInput[];
  /*
   * This is a callback to edit the description field, when not passed - the description will be not be visible if unavailable
   */
  handleEditDescription?: () => void;
  onOpenFullDescription: () => void;
  isUpdateMode?: boolean;
  isPreviewMode?: boolean;
  secretFieldsState?: SecretFieldsState;
};

const DeploymentForm = ({
  item,
  isPublished,
  hasPreview,
  hasErrors,
  errors,
  inputs,
  loading,
  isUpdateMode,
  isPreviewMode,
  secretFieldsState,
}: DeploymentFormFormProps) => {
  const { register, watch, formState } = useFormContext<VersionTemplateFormFields>();
  const deploymentName = watch("name");
  const nameError = formState.errors.name;
  const showErrors = isPreviewMode && hasErrors;

  useAnnounce(showErrors ? errors.join(". ") : undefined);

  return (
    <Box direction="column" fullWidth gap="large">
      {isPublished && item.deprecatedAt && (
        <Banner variant="warning" title="This template version is deprecated.">
          {item.deprecationNote ? ` ${item.deprecationNote}` : ""}
        </Banner>
      )}

      {isUpdateMode ? (
        <FormFieldViewText label="Name" value={deploymentName || ""} />
      ) : (
        <FormField label="Name" error={nameError?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="Enter deployment name here..."
              {...register("name", { required: "Name field is required." })}
              {...ariaInputProps}
              error={!!nameError}
            />
          )}
        </FormField>
      )}

      {isPreviewMode && !hasPreview && !hasErrors && (
        <Banner variant="info">
          No preview available. Please add some inputs to the template.
        </Banner>
      )}

      {showErrors &&
        errors.map((error, index) => (
          <Banner className={styles.errorBanner} variant="danger" key={index}>
            {error}
          </Banner>
        ))}

      {loading && <TemplatePreviewSkeleton />}
      {!loading && !hasErrors && (
        <Box direction="column" grow="1" fullWidth gap="large">
          {inputs.map((input) => (
            <DeploymentFormField
              key={input.id}
              input={input}
              editMode={isUpdateMode}
              secretFieldsState={secretFieldsState}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DeploymentForm;
